import { z } from 'zod';
/**
 * Vocabulary and request schemas of the client portal (Mandantenportal 01–22, 27–29) shared by the API (validation)
 * and the SPA (forms, filter chips). German strings verbatim from the screens; identifiers English
 * (docs/glossary.md). Response shapes: docs/dev/api-portal.md.
 */
// --- activation and login (Mandantenportal 08–13) --------------------------------------------------------
/** Mandantenportal 08: „Mindestens 10 Zeichen und eine Zahl." */
export const portalPasswordRules = { minLength: 10 };
export const PORTAL_PASSWORD_RULE_MESSAGE = 'Mindestens 10 Zeichen und eine Zahl.';
export const PORTAL_PASSWORD_MISMATCH_MESSAGE = 'Die Passwörter stimmen nicht überein.';
/** True when the password meets the rule of Mandantenportal 08 (≥ 10 characters with at least one digit). */
export function portalPasswordValid(password) {
    return password.length >= portalPasswordRules.minLength && /\d/.test(password);
}
const portalPassword = z
    .string()
    .max(200)
    .refine(portalPasswordValid, { message: PORTAL_PASSWORD_RULE_MESSAGE });
/** Link tokens: 32 random bytes, base64url (43 characters). */
export const portalTokenSchema = z
    .string()
    .min(20)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/);
export const portalSetPasswordBodySchema = z
    .object({ password: portalPassword, passwordRepeat: z.string().max(200) })
    .refine((body) => body.password === body.passwordRepeat, {
    message: PORTAL_PASSWORD_MISMATCH_MESSAGE,
    path: ['passwordRepeat'],
});
export const portalMagicResendBodySchema = z.object({ token: portalTokenSchema });
const portalEmail = z
    .string()
    .trim()
    .toLowerCase()
    .email('Bitte eine gültige E-Mail-Adresse angeben.')
    .max(254);
export const portalLoginBodySchema = z.object({
    email: portalEmail,
    password: z.string().min(1, 'Bitte geben Sie Ihr Passwort ein.').max(200),
    /** „Angemeldet bleiben" (Mandantenportal 11): 30 days instead of 24 hours. */
    remember: z.boolean().default(false),
});
export const portalCodeLength = 6;
export const portalLoginCodeBodySchema = z.object({
    challengeId: z.string().min(1).max(300),
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Bitte geben Sie die sechsstellige Zahl ein.'),
});
export const portalCodeResendBodySchema = z.object({ challengeId: z.string().min(1).max(300) });
export const portalPasswordForgotBodySchema = z.object({ email: portalEmail });
export const portalCodeChannels = ['sms', 'email'];
/** „Bestätigungscode per SMS aktiv" (Mandantenportal 20). */
export const portalCodeChannelLabels = {
    sms: 'Bestätigungscode per SMS aktiv',
    email: 'Bestätigungscode per E-Mail aktiv',
};
// --- portal pages (Mandantenportal 14–21) ----------------------------------------------------------------
/** The six stations of „Ihr Weg zum Eigentum" (Mandantenportal 14/16/17). */
export const portalJourneySteps = [
    'first_meeting',
    'self_disclosure',
    'consultation',
    'viewing',
    'notary',
    'handover',
];
export const portalJourneyStepLabels = {
    first_meeting: 'Erstgespräch',
    self_disclosure: 'Selbstauskunft',
    consultation: 'Beratung',
    viewing: 'Besichtigung',
    notary: 'Notartermin',
    handover: 'Übergabe',
};
export const portalJourneyStates = ['done', 'customer', 'advisor', 'open'];
/** Filter chips of Mandantenportal 18. */
export const portalDocumentFilters = ['all', 'mine', 'shared', 'unit'];
export const portalDocumentsQuerySchema = z.object({
    filter: z.enum(portalDocumentFilters).default('all'),
});
/** The fields the customer may change in the profile (Mandantenportal 21); the name is read-only. */
export const portalProfileFields = ['phone', 'address', 'net_income', 'special_payments', 'equity'];
export const portalProfileFieldLabels = {
    phone: 'Telefon',
    address: 'Anschrift',
    net_income: 'Nettoeinkommen',
    special_payments: 'Sonderzahlungen jährlich',
    equity: 'Eigenkapital',
};
/** Fields whose change affects the financing volume (the banner of Mandantenportal 21). */
export const portalFinancialFields = [
    'net_income',
    'special_payments',
    'equity',
];
const amountCents = z
    .number({ message: 'Bitte tragen Sie einen Betrag ein.' })
    .int()
    .min(0, 'Bitte tragen Sie einen Betrag ein.')
    .max(100_000_000_000);
export const portalAddressSchema = z.object({
    street: z.string().trim().min(1, 'Bitte geben Sie Straße und Hausnummer an.').max(200),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}$/, 'Bitte eine fünfstellige Postleitzahl angeben.'),
    city: z.string().trim().min(1, 'Bitte geben Sie den Ort an.').max(120),
});
export const portalProfileChangeSchema = z.discriminatedUnion('field', [
    z.object({
        field: z.literal('phone'),
        value: z.string().trim().min(6, 'Bitte eine Telefonnummer angeben.').max(40),
    }),
    z.object({ field: z.literal('address'), value: portalAddressSchema }),
    z.object({ field: z.literal('net_income'), value: amountCents }),
    z.object({ field: z.literal('special_payments'), value: amountCents }),
    z.object({ field: z.literal('equity'), value: amountCents }),
]);
export const portalProfileChangesBodySchema = z.object({
    changes: z
        .array(portalProfileChangeSchema)
        .min(1, 'Bitte ändern Sie mindestens eine Angabe.')
        .max(portalProfileFields.length)
        .refine((changes) => new Set(changes.map((change) => change.field)).size === changes.length, {
        message: 'Jedes Feld nur einmal angeben.',
    }),
});
export const portalChangePasswordBodySchema = z.object({
    current: z.string().min(1, 'Bitte geben Sie Ihr bisheriges Passwort ein.').max(200),
    next: portalPassword,
});
// --- staff (Mandantenportal 27, 29) ----------------------------------------------------------------------
export const portalAccountStates = ['none', 'created', 'invited', 'active', 'locked'];
export const portalChangeRequestRejectBodySchema = z.object({
    note: z.string().trim().min(1, 'Bitte geben Sie einen Grund an.').max(1000),
});
//# sourceMappingURL=portal.js.map